import React, { useEffect, useState } from 'react';
import { CaretDown, SidebarSimple } from '@phosphor-icons/react';
import { APP_NAV, JOURNEY_NAV_IDS, useAppNavigation } from '../../context/AppNavigationContext';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { PUBLIC_IMAGES } from '../../constants/publicAssets';
import {
  ADMIN_NAV_ITEMS,
  CUSTOMER_NAV_ITEMS,
  SIDEBAR_ACTION_ICONS,
} from './sidebarNav';
import { cn } from '../../lib/utils';

export const SIDEBAR_EXPANDED_WIDTH = 260;
export const SIDEBAR_COLLAPSED_WIDTH = 80;
export const SIDEBAR_INSET = 12;

const ICON_SIZE = 22;

function NavItemIcon({ item }) {
  const Icon = item.icon;
  if (!Icon) return null;
  return (
    <Icon
      size={ICON_SIZE}
      weight="duotone"
      className="shrink-0"
      aria-hidden="true"
    />
  );
}

function NavButton({ item, isActive, onClick, nested = false, collapsed = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      className={cn(
        'font-display flex w-full items-center rounded-lg text-left text-sm font-semibold transition-colors',
        collapsed ? 'justify-center px-2 py-3' : nested ? 'gap-3 px-3 py-2.5' : 'gap-3.5 px-3 py-3',
        isActive
          ? 'bg-white/20 text-white'
          : 'text-white/85 hover:bg-white/10 hover:text-white'
      )}
      aria-current={isActive ? 'page' : undefined}
    >
      <NavItemIcon item={item} />
      {!collapsed ? <span className="truncate">{item.label}</span> : null}
    </button>
  );
}

function SidebarLogo({ src, isCustom, collapsed }) {
  return (
    <img
      key={src}
      src={src}
      alt={isCustom ? 'Customer logo' : 'Venue Intelligence'}
      className={cn(
        'object-contain',
        collapsed ? 'h-10 w-10' : 'h-10 max-w-[160px] object-left',
        !isCustom && 'brightness-0 invert'
      )}
    />
  );
}

function CollapseToggle({ collapsed, onToggle }) {
  return (
    <button
      type="button"
      className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white/85 transition hover:bg-white/10 hover:text-white lg:inline-flex"
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-expanded={!collapsed}
      onClick={onToggle}
    >
      <SidebarSimple size={22} weight="duotone" mirrored={collapsed} aria-hidden="true" />
    </button>
  );
}

export function Sidebar({
  mobileOpen,
  onMobileClose,
  onLogout,
  collapsed = false,
  onCollapsedChange,
}) {
  const { activeNav, setActiveNav, setChatOpen } = useAppNavigation();
  const { isSuperAdmin } = useAuth();
  const { logoUrl } = useTheme();
  const [journeysOpen, setJourneysOpen] = useState(() => JOURNEY_NAV_IDS.includes(activeNav));
  const SignOutIcon = SIDEBAR_ACTION_ICONS.signOut;

  useEffect(() => {
    if (JOURNEY_NAV_IDS.includes(activeNav)) {
      setJourneysOpen(true);
    }
  }, [activeNav]);

  const navItems = isSuperAdmin
    ? [...CUSTOMER_NAV_ITEMS, ...ADMIN_NAV_ITEMS]
    : CUSTOMER_NAV_ITEMS;
  const sidebarLogo = logoUrl || PUBLIC_IMAGES.logoBlack;
  const isCustomLogo = Boolean(logoUrl);
  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  function handleNavClick(itemId) {
    setActiveNav(itemId);
    setChatOpen(false);
    onMobileClose?.();
  }

  function handleLogout() {
    onMobileClose?.();
    onLogout?.();
  }

  return (
    <aside
      className={cn(
        'fixed z-40 flex flex-col bg-[var(--brand-primary,#1A428A)] text-white shadow-[0_12px_40px_rgba(15,23,42,0.28)] transition-all duration-200 ease-out',
        'top-3 bottom-3 left-3 rounded-2xl',
        'lg:translate-x-0',
        mobileOpen ? 'translate-x-0' : '-translate-x-[120%] lg:translate-x-0'
      )}
      style={{ width }}
      aria-label="Main navigation"
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <div
        className={cn(
          'border-b border-white/10',
          collapsed
            ? 'flex flex-col items-center gap-3 px-2 pb-5 pt-4'
            : 'flex min-h-[4.5rem] items-center justify-between gap-2 px-3 py-3'
        )}
      >
        <a
          href="/"
          className={cn('flex min-w-0 items-center', collapsed && 'justify-center')}
          aria-label="Venue Intelligence home"
          onClick={(event) => {
            event.preventDefault();
            handleNavClick(APP_NAV.OVERVIEW);
          }}
        >
          <SidebarLogo src={sidebarLogo} isCustom={isCustomLogo} collapsed={collapsed} />
        </a>

        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => onCollapsedChange?.(!collapsed)}
        />
      </div>

      <nav
        className={cn(
          'flex-1 overflow-y-auto',
          collapsed ? 'px-2 pb-4 pt-4' : 'px-3 py-4'
        )}
      >
        <ul className="space-y-1">
          {navItems.map((item) => {
            if (item.children) {
              return (
                <li key={item.id} className="relative">
                  <button
                    type="button"
                    title={collapsed ? item.label : undefined}
                    onClick={() => setJourneysOpen((open) => !open)}
                    className={cn(
                      'font-display flex w-full items-center rounded-lg text-left text-sm font-semibold transition-colors',
                      collapsed ? 'justify-center px-2 py-3' : 'gap-3.5 px-3 py-3',
                      'text-white/85 hover:bg-white/10 hover:text-white'
                    )}
                    aria-expanded={journeysOpen}
                  >
                    <NavItemIcon item={item} />
                    {!collapsed ? (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        <CaretDown
                          size={16}
                          weight="bold"
                          className={cn(
                            'shrink-0 transition-transform',
                            journeysOpen ? 'rotate-180' : ''
                          )}
                          aria-hidden="true"
                        />
                      </>
                    ) : null}
                  </button>
                  {journeysOpen ? (
                    <ul className={cn('mt-1 space-y-0.5', !collapsed && 'ml-4 pl-2')}>
                      {item.children.map((child) => (
                        <li key={child.id}>
                          <NavButton
                            item={child}
                            nested={!collapsed}
                            collapsed={collapsed}
                            isActive={activeNav === child.id}
                            onClick={() => handleNavClick(child.id)}
                          />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            }

            return (
              <li key={item.id}>
                <NavButton
                  item={item}
                  collapsed={collapsed}
                  isActive={activeNav === item.id}
                  onClick={() => handleNavClick(item.id)}
                />
              </li>
            );
          })}
        </ul>
      </nav>

      <div className={cn('border-t border-white/10 py-3', collapsed ? 'px-2' : 'px-3')}>
        <button
          type="button"
          title={collapsed ? 'Sign out' : undefined}
          onClick={handleLogout}
          className={cn(
            'font-display flex w-full items-center rounded-lg text-left text-sm font-semibold text-white/85 transition-colors hover:bg-white/10 hover:text-white',
            collapsed ? 'justify-center px-2 py-3' : 'gap-3.5 px-3 py-3'
          )}
        >
          <SignOutIcon size={ICON_SIZE} weight="duotone" className="shrink-0" aria-hidden="true" />
          {!collapsed ? <span>Sign out</span> : null}
        </button>
      </div>
    </aside>
  );
}
